import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'focus-within-polyfill';
import 'vite/modulepreload-polyfill';

import './config/polyfills';
import './config/datadog';

import '@socialchorus/shared-ui-components/style';
import '@socialchorus/solar-lightbox/style';

import ReactDOM from 'react-dom';
import api from './config/api';
import apiFactory from './config/api-factory';
import eva from './config/eva';
import twig from './config/twig';
import { patron } from './services/feature-flags';
import { AxiosError } from 'axios';

import Patron from './patron';
import { resolveProgramPath } from './config/program-basename';

import * as serviceWorker from './serviceWorker';

import './config/i18n';
import NamespacedLocalStorage from './lib/localstorage-namespaced';
import { localStorageEnabled, removeStorageItem } from './lib/localstorage';
import {
  generateOfflineQueriesKey,
  isOfflineQueriesKey,
} from './lib/offline-queries-cache';
import { optimus } from './components/v2/search/http';
import { buildAuthRedirectSearch } from './lib/auth-redirect';
import { Program } from './models/program/types';
import {
  SessionData,
  AppConfig,
  AuthRedirectResponse,
  BootstrapData,
} from './models/bootstrap/types';

/**
 * Handles redirection to auth URL when bootstrap data call returns 401
 * @param {string} authUrl - The authentication URL to redirect to
 * @param {string} programPath - The program path to use for redirection
 * @returns {string} The complete redirect URL
 */
const processRedirectUrl = (authUrl: string, programPath: string): string => {
  // Extract path from current location, similar to auth.js logic
  const programPathRegex = new RegExp(programPath, 'i');

  const route = window.location.pathname
    .replace(/\/sc4$/, '')
    .replace(programPathRegex, '')
    .replace(/^\//, '');

  // Create hash fragment since we are not stripping path
  const hash =
    '#' + route + window.location.hash.replace(/#/, route ? '/' : '');

  const search = buildAuthRedirectSearch(window.location.search);

  return authUrl + (search ? `?${search}` : '') + hash;
};

/**
 * Fetches the current session data from the API
 * @returns {Promise<SessionData>} Promise resolving to session data
 */
const getSessionData = (): Promise<SessionData> => {
  return api.post('/auth').then((res) => res.data);
};

/**
 * Sets auth headers using session data to ensure authenticated requests
 * @param {SessionData} sessionData - The session data containing session_id
 * @returns {SessionData} The original session data
 */
const setSessionAuthHeaders = (sessionData: SessionData): SessionData => {
  const { session_id: sessionId = undefined } = sessionData;

  // Set initial auth headers for subsequent requests
  const initialAuthHeaders = {
    Authorization: `SocialChorusAuth sessionId='${sessionId}'`,
    'x-api-version': '3.5.0',
    'x-sc-platform': 'web',
  };

  api.defaults.headers.common = initialAuthHeaders;

  return sessionData;
};

/**
 * Fetches bootstrap data based on URL information with error handling for 401 responses
 * @returns {Promise<{ data: Partial<Omit<BootstrapData, "session">> }>} Promise resolving to bootstrap data
 */
const getBootstrapData = (): Promise<{
  data: Partial<Omit<BootstrapData, 'session'>>;
}> => {
  const patronDefaultHostname = import.meta.env.VITE_APP_PATRON_DOMAIN;
  const customDomain = window.location.hostname !== patronDefaultHostname;

  const errorHandler = (error: unknown) => {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as AxiosError<AuthRedirectResponse>;
      const response = axiosError.response;

      if (response && response.status === 401 && response.data) {
        const authData = response.data;

        if (authData.authUrl) {
          // Redirect to auth URL provided in the response
          const redirectUrl = processRedirectUrl(
            authData.authUrl,
            authData.programPath
          );
          window.location.href = redirectUrl;
          // Return a never-resolving promise to prevent further execution
          // This is intentionally empty as we're handling the redirect elsewhere
          return new Promise<never>(() => {
            /* intentionally empty - app will redirect */
          });
        }
      }
    }
    // For other errors, just propagate the rejection
    return Promise.reject(error);
  };

  if (customDomain) {
    return api.get('/bootstrap-data').catch(errorHandler);
  } else {
    // Using standard regex capture groups instead of named capture groups for ES2015 compatibility
    const pathRegex = /^\/([^/]*)\/([^/]*)\/?/;
    const matches = pathRegex.exec(window.location.pathname);

    if (matches && matches.length >= 3) {
      const brandSlug = matches[1];
      const programSlug = matches[2];
      return api
        .get(`/${brandSlug}/${programSlug}/bootstrap-data`)
        .catch(errorHandler);
    } else {
      return Promise.reject('Unable to parse program slugs from url');
    }
  }
};

/**
 * Combines session data with bootstrap data
 * @param {SessionData} sessionData - The session data
 * @returns {Promise<BootstrapData>} Promise resolving to combined data
 */
const combineBootstrapData = (
  sessionData: SessionData
): Promise<BootstrapData> =>
  getBootstrapData().then((res) => {
    // Ensure the result has the required properties to form a valid BootstrapData
    return {
      session: sessionData,
      program: res.data.program as Program,
      config: res.data.config as AppConfig,
      ...res.data,
    } as BootstrapData;
  });

/**
 * Reconciles the program path in the bootstrap payload against the URL it was served
 * for.
 *
 * A payload can disagree with its URL — a shared cache entry populated for a different
 * path, or a stale copy served while the program lookup was failing. Reconciling here,
 * before the config reaches Redux, fixes the router basename and all four
 * program-path-relative link helpers from one place; guarding only the router would
 * leave the helpers reading the wrong value.
 *
 * @param {BootstrapData} bootstrapData - The bootstrap data
 * @returns {BootstrapData} The bootstrap data, with a program path that matches the URL
 */
const reconcileProgramPath = (bootstrapData: BootstrapData): BootstrapData => {
  const { config, program } = bootstrapData;
  if (!config) return bootstrapData;

  const programPath = resolveProgramPath(
    config.program_path,
    window.location.pathname
  );
  if (programPath === config.program_path) return bootstrapData;

  // program.root_path feeds internal-link checks and auth redirects. Only correct it
  // when it agreed with program_path to begin with, so custom-domain programs — which
  // have an empty program_path but a real root_path — are left alone.
  const shouldFixRootPath =
    typeof program?.root_path === 'string' &&
    program.root_path.toLowerCase() === config.program_path?.toLowerCase();

  return {
    ...bootstrapData,
    config: { ...config, program_path: programPath },
    ...(shouldFixRootPath
      ? { program: { ...program, root_path: programPath } }
      : {}),
  };
};

/**
 * Sets all API request defaults based on bootstrap data
 * @param {BootstrapData} bootstrapData - The bootstrap data
 * @returns {BootstrapData} The bootstrap data
 */
const setRequestDefaults = (bootstrapData: BootstrapData): BootstrapData => {
  const {
    config: {
      api_url: apiUrl = '',
      eva_url: evaUrl = '',
      twig_url: twigUrl = '',
      optimus_url: optimusUrl = '',
    } = {},
    session: { session_id: sessionId = '' } = {},
    program: { id: programId = '' } = {},
  } = bootstrapData;

  const authHeaders = {
    Authorization: `SocialChorusAuth sessionId='${sessionId}'`,
    'x-api-version': '3.5.0',
    'x-sc-platform': 'web',
  };

  api.defaults.baseURL = `${apiUrl}/v2/programs/${programId}`;
  api.defaults.headers.common = authHeaders;

  apiFactory.apiUrl = apiUrl;
  apiFactory.programId = programId;
  apiFactory.authHeaders = authHeaders;

  eva.defaults.baseURL = `${evaUrl}/v1/programs/${programId}`;
  eva.defaults.headers.common = authHeaders;

  twig.defaults.baseURL = twigUrl;

  optimus.defaults.baseURL = optimusUrl;
  optimus.defaults.headers.common = authHeaders;

  patron.defaults.headers.common = authHeaders;

  return bootstrapData;
};

/**
 * Cleans up stale offline-queries cache entries from localStorage
 * @param {BootstrapData} bootstrapData - The bootstrap data
 * @returns {BootstrapData} The bootstrap data
 */
const cleanupOfflineQueries = (bootstrapData: BootstrapData): BootstrapData => {
  if (!localStorageEnabled()) return bootstrapData;

  const {
    session: { advocate_id },
    program: { id: program_id },
  } = bootstrapData;

  const currentKey = generateOfflineQueriesKey(program_id, advocate_id);

  Object.keys(window.localStorage)
    .filter((key) => {
      // Match any offline-queries-* key
      if (!isOfflineQueriesKey(key)) return false;
      // Keep only the current advocate/program combination
      return key !== currentKey;
    })
    .forEach(removeStorageItem);

  return bootstrapData;
};

/**
 * Processes namespaced local storage based on bootstrap data
 * @param {BootstrapData} bootstrapData - The bootstrap data
 * @returns {BootstrapData} The bootstrap data
 */
const processNamespacedLocalStorage = (
  bootstrapData: BootstrapData
): BootstrapData => {
  if (!localStorageEnabled()) return bootstrapData;

  const {
    session: { advocate_id, program_id },
  } = bootstrapData;

  const namespacedStorage = new NamespacedLocalStorage({
    advocate_id: String(advocate_id),
    program_id: String(program_id),
  });
  Object.keys(window.localStorage)
    .filter(
      (key) =>
        NamespacedLocalStorage.isNamespacedEntry(key) &&
        !namespacedStorage.isKeyAccessible(key)
    )
    .forEach(removeStorageItem);

  return bootstrapData;
};

/**
 * Mounts the React application with bootstrap data
 * @param {BootstrapData} bootstrapData - The bootstrap data
 */
const mountApp = (bootstrapData: BootstrapData): void => {
  ReactDOM.render(
    <Patron bootstrapData={bootstrapData} />,
    document.getElementById('patron')
  );
};

// Updated initialization chain to ensure auth headers are set before bootstrap data request
getSessionData()
  .then(setSessionAuthHeaders)
  .then(combineBootstrapData)
  .then(reconcileProgramPath)
  .then(setRequestDefaults)
  .then(cleanupOfflineQueries)
  .then(processNamespacedLocalStorage)
  .then(mountApp)
  .catch((error: unknown) => {
    console.error('Failed to initialize application:', error);
    // Could add fallback UI rendering here if needed
  });

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
